import type { ExchangeConnection, ExchangeFreshnessEntry } from '../api/exchange';
import type { ApiConnection } from '../api/exchange-sync';
import { getExchangeFreshnessRecoveryLabel, getExchangeFreshnessRecoveryVerdictKey } from './exchangeFreshnessRecovery';
import {
  AUTH_SESSION_BLOCKED_MESSAGE,
  SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE,
} from './sourceProof';

export type ExchangeHealthState = 'healthy' | 'stale' | 'blocked' | 'never_synced';

export type ExchangeHealthReason =
  | 'recent_successful_sync'
  | 'stale_sync'
  | 'sync_error'
  | 'never_synced'
  | 'provider_credentials_required'
  | 'provider_auth_required'
  | 'provider_replay_blocked';

export type ExchangeFreshnessBucket = 'fresh' | 'stale' | 'never_synced';
export type ExchangeBlockerClassification = 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
export type ExchangeRunnerWaitState = 'runner_limited_wait' | null;
export type ExchangeFreshnessState = 'healthy' | 'stale_verified' | 'never_synced';
export type ExchangePrimaryNextStep = 'sync_now' | 'retry_sync' | 'manage_connection' | null;
export type ExchangeDominantAction =
  | 'review_last_successful_sync'
  | 'sync_now'
  | 'retry_sync'
  | 'manage_connection'
  | null;
export type ExchangeHealthBlocker = 'SAFE_EXCHANGE_REPLAY_BLOCKED' | null;
export type ExchangeHealthTone = 'info' | 'success' | 'warning' | 'error';

export interface ExchangeLastSuccessfulSyncProof {
  available: boolean;
  syncedAt: string | null;
  freshnessBucket: ExchangeFreshnessBucket;
  freshnessStaleHours: number | null;
  proofSource: 'exchange_api_connections';
}

export interface ExchangeSafeRetryGuidance {
  code: string;
  retryableNow: boolean;
  retryAction: ExchangePrimaryNextStep;
  dominantAction: ExchangeDominantAction;
}

export interface ExchangeRouteGuidanceEntry {
  routeRole: 'proof_owner' | 'management_owner' | 'summary_only' | 'ledger_only';
  handoffRoute: '/exchange-sync' | null;
  primaryNextStep: ExchangePrimaryNextStep;
  dominantAction: ExchangeDominantAction;
  safeRetryGuidance: ExchangeSafeRetryGuidance;
}

export interface ExchangeSyncHealth {
  exchange: string;
  connectionId: string | null;
  lastSuccessfulSyncAt: string | null;
  staleHours: number | null;
  freshnessBucket: ExchangeFreshnessBucket;
  healthState: ExchangeHealthState;
  healthReason: ExchangeHealthReason;
  blockerCode: ExchangeHealthBlocker;
  blockerClassification: ExchangeBlockerClassification;
  primaryNextStep: ExchangePrimaryNextStep;
  proofSource: 'exchange_api_connections';
  lastSyncImportedCount: number | null;
  lastSyncSkippedCount: number | null;
  lastSyncErrorCount: number | null;
  lastSyncSourceLabel: string | null;
  dominantAction: ExchangeDominantAction;
  lastSuccessfulSyncProof: ExchangeLastSuccessfulSyncProof;
  freshnessState: ExchangeFreshnessState;
  credentialActionRequired: boolean;
  providerAuthActionRequired: boolean;
  providerReplayBlocked: boolean;
  runnerWaitState: ExchangeRunnerWaitState;
  runnerWaitMessage: string | null;
  safeRetryGuidance: ExchangeSafeRetryGuidance;
  proofAuthority: 'mounted_last_successful_sync' | null;
  providerReplayScope: 'management_owned_path' | null;
  routeGuidance: {
    import: ExchangeRouteGuidanceEntry;
    management: ExchangeRouteGuidanceEntry;
    summary: ExchangeRouteGuidanceEntry;
    ledger: ExchangeRouteGuidanceEntry;
  } | null;
}

interface ResolveExchangeHealthInput {
  surface?: 'import' | 'management' | 'freshness';
  exchange: string;
  connectionId: string | null;
  lastSuccessfulSyncAt?: string | null;
  syncStatus?: string | null;
  errorMessage?: string | null;
  healthState?: ExchangeHealthState | null;
  healthReason?: ExchangeHealthReason | null;
  healthBlocker?: ExchangeHealthBlocker | null;
  blockerClassification?: ExchangeBlockerClassification | null;
  freshnessBucket?: ExchangeFreshnessBucket | null;
  freshnessStaleHours?: number | null;
  freshnessState?: ExchangeFreshnessState | null;
  primaryNextStep?: ExchangePrimaryNextStep | null;
  proofSource?: 'exchange_api_connections' | null;
  lastSyncImportedCount?: number | null;
  lastSyncSkippedCount?: number | null;
  lastSyncErrorCount?: number | null;
  lastSyncSourceLabel?: string | null;
  dominantAction?: ExchangeDominantAction | null;
  lastSuccessfulSyncProof?: {
    available: boolean;
    syncedAt?: string | null;
    freshnessBucket: ExchangeFreshnessBucket;
    freshnessStaleHours: number | null;
    proofSource: 'exchange_api_connections';
  } | null;
  credentialActionRequired?: boolean | null;
  providerAuthActionRequired?: boolean | null;
  providerReplayBlocked?: boolean | null;
  runnerWaitState?: ExchangeRunnerWaitState;
  runnerWaitMessage?: string | null;
  safeRetryGuidance?: {
    code: string;
    retryableNow: boolean;
    retryAction: ExchangePrimaryNextStep;
    dominantAction: ExchangeDominantAction;
  } | null;
  proofAuthority?: 'mounted_last_successful_sync' | null;
  providerReplayScope?: 'management_owned_path' | null;
  routeGuidance?: {
    import: ExchangeRouteGuidanceEntry;
    management: ExchangeRouteGuidanceEntry;
    summary: ExchangeRouteGuidanceEntry;
    ledger: ExchangeRouteGuidanceEntry;
  } | null;
}

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const REPLAY_BLOCKED_PATTERN =
  /(safe_exchange_replay_blocked|replay blocked|replay limit|nonce|recvwindow|timestamp(?:.*outside|.*ahead))/i;
const NON_PROVIDER_RUNTIME_LIMIT_PATTERN =
  /(missing safe read-only exchange api credentials on this runner|safe credential replay|credential replay blocked)/i;
const CREDENTIAL_BLOCKED_PATTERN =
  /(credential|api key|api secret|passphrase|decrypt|invalid key|missing key|signature)/i;
const AUTH_BLOCKED_PATTERN =
  /(unauthorized|forbidden|permission|auth|authentication|session|sign[- ]?in|required|expired)/i;

function normalizeTimestamp(value: string | Date | null | undefined): string | null {
  if (!value) return null;

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
}

function classifyBlockedReason(errorMessage: string | null | undefined): ExchangeHealthReason | null {
  if (!errorMessage) {
    return null;
  }

  if (NON_PROVIDER_RUNTIME_LIMIT_PATTERN.test(errorMessage)) {
    return null;
  }

  if (REPLAY_BLOCKED_PATTERN.test(errorMessage)) {
    return 'provider_replay_blocked';
  }

  if (CREDENTIAL_BLOCKED_PATTERN.test(errorMessage)) {
    return 'provider_credentials_required';
  }

  if (AUTH_BLOCKED_PATTERN.test(errorMessage)) {
    return 'provider_auth_required';
  }

  return null;
}

function deriveRunnerWaitState(errorMessage: string | null | undefined): ExchangeRunnerWaitState {
  if (!errorMessage) {
    return null;
  }

  return NON_PROVIDER_RUNTIME_LIMIT_PATTERN.test(errorMessage) ? 'runner_limited_wait' : null;
}

function deriveRunnerWaitMessage(
  runnerWaitState: ExchangeRunnerWaitState,
  message: string | null | undefined,
): string | null {
  if (!runnerWaitState) {
    return null;
  }

  return (
    message ??
    'This runner cannot safely replay the saved read-only exchange connection right now. Wait for a compatible runner before expecting a fresh refresh attempt.'
  );
}

function deriveExchangePrimaryNextStep(
  surface: 'import' | 'management' | 'freshness',
  healthState: ExchangeHealthState,
  healthReason: ExchangeHealthReason,
): ExchangePrimaryNextStep {
  if (healthState === 'healthy') {
    return null;
  }

  if (surface === 'management' && healthState === 'blocked') {
    return 'manage_connection';
  }

  if (healthState === 'blocked') {
    return surface === 'freshness' ? 'manage_connection' : 'retry_sync';
  }

  if (healthReason === 'sync_error') {
    return 'retry_sync';
  }

  return 'sync_now';
}

function deriveBlockerClassification(
  healthReason: ExchangeHealthReason,
  blockerCode: ExchangeHealthBlocker | null | undefined,
): ExchangeBlockerClassification {
  if (blockerCode === 'SAFE_EXCHANGE_REPLAY_BLOCKED' || healthReason === 'provider_replay_blocked') {
    return 'safe_exchange_replay_blocked';
  }

  if (healthReason === 'provider_auth_required') {
    return 'auth_action';
  }

  if (healthReason === 'provider_credentials_required') {
    return 'credential_action';
  }

  return null;
}

function deriveFreshnessState(
  freshnessBucket: ExchangeFreshnessBucket,
): ExchangeFreshnessState {
  switch (freshnessBucket) {
    case 'stale':
      return 'stale_verified';
    case 'never_synced':
      return 'never_synced';
    case 'fresh':
    default:
      return 'healthy';
  }
}

function deriveExchangeDominantAction(
  surface: 'import' | 'management' | 'freshness',
  healthState: ExchangeHealthState,
  primaryNextStep: ExchangePrimaryNextStep,
  hasLastSuccessfulSyncProof: boolean,
): ExchangeDominantAction {
  if (surface === 'management') {
    if (healthState === 'healthy') {
      return null;
    }

    return primaryNextStep === 'sync_now' || primaryNextStep === 'retry_sync'
      ? primaryNextStep
      : 'manage_connection';
  }

  if (hasLastSuccessfulSyncProof && healthState === 'blocked') {
    return 'review_last_successful_sync';
  }

  return primaryNextStep;
}

function deriveSafeRetryGuidance(
  healthState: ExchangeHealthState,
  healthReason: ExchangeHealthReason,
  primaryNextStep: ExchangePrimaryNextStep,
  dominantAction: ExchangeDominantAction,
  runnerWaitState: ExchangeRunnerWaitState,
): ExchangeSafeRetryGuidance {
  if (runnerWaitState) {
    return {
      code: 'wait_for_runner_window',
      retryableNow: false,
      retryAction: null,
      dominantAction,
    };
  }

  if (healthState === 'healthy') {
    return {
      code: 'none',
      retryableNow: false,
      retryAction: null,
      dominantAction,
    };
  }

  if (healthState === 'stale') {
    return {
      code: 'retry_now',
      retryableNow: true,
      retryAction: primaryNextStep ?? 'sync_now',
      dominantAction,
    };
  }

  if (healthState === 'never_synced') {
    return {
      code: 'retry_now',
      retryableNow: true,
      retryAction: primaryNextStep ?? 'sync_now',
      dominantAction,
    };
  }

  if (healthReason === 'provider_replay_blocked') {
    return {
      code: 'wait_for_provider_replay_window',
      retryableNow: false,
      retryAction: primaryNextStep,
      dominantAction,
    };
  }

  if (healthReason === 'provider_auth_required') {
    return {
      code: 'provider_auth_required',
      retryableNow: false,
      retryAction: primaryNextStep,
      dominantAction,
    };
  }

  if (healthReason === 'provider_credentials_required') {
    return {
      code: 'update_credentials_then_retry',
      retryableNow: false,
      retryAction: primaryNextStep,
      dominantAction,
    };
  }

  return {
    code: 'retry_now',
    retryableNow: true,
    retryAction: primaryNextStep,
    dominantAction,
  };
}

export function resolveExchangeHealth(input: ResolveExchangeHealthInput): ExchangeSyncHealth {
  const proofSyncedAt = normalizeTimestamp(input.lastSuccessfulSyncProof?.syncedAt);
  const lastSuccessfulSyncAt = normalizeTimestamp(input.lastSuccessfulSyncAt) ?? proofSyncedAt;
  const derivedStaleHours =
    lastSuccessfulSyncAt === null
      ? null
      : Math.floor((Date.now() - new Date(lastSuccessfulSyncAt).getTime()) / 3_600_000);
  const isStale =
    lastSuccessfulSyncAt === null
      ? true
      : Date.now() - new Date(lastSuccessfulSyncAt).getTime() > STALE_THRESHOLD_MS;
  const blockedReason = classifyBlockedReason(input.errorMessage);

  const fallbackHealthState: ExchangeHealthState =
    blockedReason
      ? 'blocked'
      : lastSuccessfulSyncAt
        ? isStale
          ? 'stale'
          : 'healthy'
        : 'never_synced';
  const fallbackHealthReason: ExchangeHealthReason =
    blockedReason
      ? blockedReason
      : lastSuccessfulSyncAt
        ? input.syncStatus === 'error' && input.errorMessage
          ? 'sync_error'
          : isStale
            ? 'stale_sync'
            : 'recent_successful_sync'
        : input.syncStatus === 'error' && input.errorMessage
          ? 'sync_error'
          : 'never_synced';
  const healthState = input.healthState ?? fallbackHealthState;
  const healthReason = input.healthReason ?? fallbackHealthReason;
  const freshnessBucket =
    input.lastSuccessfulSyncProof?.freshnessBucket ??
    input.freshnessBucket ??
    (lastSuccessfulSyncAt ? (isStale ? 'stale' : 'fresh') : 'never_synced');
  const freshnessStaleHours =
    input.lastSuccessfulSyncProof?.freshnessStaleHours ??
    input.freshnessStaleHours ??
    derivedStaleHours;
  const blockerCode = input.healthBlocker ?? (blockedReason === 'provider_replay_blocked' ? 'SAFE_EXCHANGE_REPLAY_BLOCKED' : null);
  const blockerClassification =
    input.blockerClassification ??
    deriveBlockerClassification(healthReason, blockerCode);
  const lastSuccessfulSyncProof = {
    available: input.lastSuccessfulSyncProof?.available ?? Boolean(lastSuccessfulSyncAt),
    syncedAt: lastSuccessfulSyncAt,
    freshnessBucket,
    freshnessStaleHours,
    proofSource: input.lastSuccessfulSyncProof?.proofSource ?? input.proofSource ?? 'exchange_api_connections',
  } satisfies ExchangeLastSuccessfulSyncProof;
  const primaryNextStep =
    input.primaryNextStep ??
    deriveExchangePrimaryNextStep(
      input.surface ?? 'freshness',
      healthState,
      healthReason,
    );
  const dominantAction =
    input.dominantAction ??
    deriveExchangeDominantAction(
      input.surface ?? 'freshness',
      healthState,
      primaryNextStep,
      lastSuccessfulSyncProof.available,
      );
  const freshnessState =
    input.freshnessState ??
    deriveFreshnessState(lastSuccessfulSyncProof.freshnessBucket);
  const credentialActionRequired =
    input.credentialActionRequired ??
    (blockerClassification === 'credential_action' ||
      blockerClassification === 'auth_action' ||
      healthReason === 'provider_credentials_required' ||
      healthReason === 'provider_auth_required');
  const providerAuthActionRequired =
    input.providerAuthActionRequired ??
    (blockerClassification === 'auth_action' || healthReason === 'provider_auth_required');
  const providerReplayBlocked =
    input.providerReplayBlocked ??
    (blockerClassification === 'safe_exchange_replay_blocked' || healthReason === 'provider_replay_blocked');
  const runnerWaitState = input.runnerWaitState ?? deriveRunnerWaitState(input.errorMessage);
  const runnerWaitMessage = deriveRunnerWaitMessage(
    runnerWaitState,
    input.runnerWaitMessage,
  );
  const safeRetryGuidance =
    input.safeRetryGuidance ??
    deriveSafeRetryGuidance(healthState, healthReason, primaryNextStep, dominantAction, runnerWaitState);

  return {
    exchange: input.exchange,
    connectionId: input.connectionId,
    lastSuccessfulSyncAt,
    staleHours: freshnessStaleHours,
    freshnessBucket,
    healthState,
    healthReason,
    blockerCode,
    blockerClassification,
    primaryNextStep,
    proofSource: input.proofSource ?? 'exchange_api_connections',
    lastSyncImportedCount: input.lastSyncImportedCount ?? null,
    lastSyncSkippedCount: input.lastSyncSkippedCount ?? null,
    lastSyncErrorCount: input.lastSyncErrorCount ?? null,
    lastSyncSourceLabel: input.lastSyncSourceLabel ?? null,
    dominantAction,
    lastSuccessfulSyncProof,
    freshnessState,
    credentialActionRequired,
    providerAuthActionRequired,
    providerReplayBlocked,
    runnerWaitState,
    runnerWaitMessage,
    safeRetryGuidance,
    proofAuthority: input.proofAuthority ?? 'mounted_last_successful_sync',
    providerReplayScope: input.providerReplayScope ?? null,
    routeGuidance: input.routeGuidance ?? null,
  };
}

export function getApiConnectionHealth(connection: ApiConnection | null | undefined): ExchangeSyncHealth | null {
  if (!connection) return null;

  return resolveExchangeHealth({
    surface: 'import',
    exchange: connection.exchange,
    connectionId: connection.id,
    lastSuccessfulSyncAt: connection.last_sync_completed_at ?? connection.last_synced_at,
    syncStatus: connection.sync_status,
    errorMessage: connection.error_message,
    healthState: connection.health_state,
    healthReason: connection.health_reason,
    healthBlocker: connection.health_blocker,
    blockerClassification: connection.blocker_classification,
    freshnessBucket: connection.freshness_bucket,
    freshnessStaleHours: connection.freshness_stale_hours,
    freshnessState: connection.freshness_state,
    primaryNextStep: connection.primary_next_step,
    proofSource: 'exchange_api_connections',
    lastSyncImportedCount: connection.last_sync_imported_count,
    lastSyncSkippedCount: connection.last_sync_skipped_count,
    lastSyncErrorCount: connection.last_sync_error_count,
    lastSyncSourceLabel: connection.last_sync_source_label,
    dominantAction: connection.dominant_action,
    lastSuccessfulSyncProof: connection.last_successful_sync_proof
      ? {
          available: connection.last_successful_sync_proof.available,
          syncedAt: connection.last_successful_sync_proof.synced_at,
          freshnessBucket: connection.last_successful_sync_proof.freshness_bucket,
          freshnessStaleHours: connection.last_successful_sync_proof.freshness_stale_hours,
          proofSource: connection.last_successful_sync_proof.proof_source,
        }
      : null,
    credentialActionRequired: connection.credential_action_required,
    providerAuthActionRequired: connection.provider_auth_action_required,
    providerReplayBlocked: connection.provider_replay_blocked,
    runnerWaitState: connection.runner_wait_state,
    runnerWaitMessage: connection.runner_wait_message,
    safeRetryGuidance: connection.safe_retry_guidance
      ? {
          code: connection.safe_retry_guidance.code,
          retryableNow: connection.safe_retry_guidance.retryable_now,
          retryAction: connection.safe_retry_guidance.retry_action,
          dominantAction: connection.safe_retry_guidance.dominant_action,
        }
      : null,
    proofAuthority: connection.proof_authority ?? null,
    providerReplayScope: connection.provider_replay_scope ?? null,
    routeGuidance: connection.route_guidance
      ? {
          import: {
            routeRole: connection.route_guidance.import.route_role,
            handoffRoute: connection.route_guidance.import.handoff_route,
            primaryNextStep: connection.route_guidance.import.primary_next_step,
            dominantAction: connection.route_guidance.import.dominant_action,
            safeRetryGuidance: {
              code: connection.route_guidance.import.safe_retry_guidance.code,
              retryableNow: connection.route_guidance.import.safe_retry_guidance.retryable_now,
              retryAction: connection.route_guidance.import.safe_retry_guidance.retry_action,
              dominantAction: connection.route_guidance.import.safe_retry_guidance.dominant_action,
            },
          },
          management: {
            routeRole: connection.route_guidance.management.route_role,
            handoffRoute: connection.route_guidance.management.handoff_route,
            primaryNextStep: connection.route_guidance.management.primary_next_step,
            dominantAction: connection.route_guidance.management.dominant_action,
            safeRetryGuidance: {
              code: connection.route_guidance.management.safe_retry_guidance.code,
              retryableNow: connection.route_guidance.management.safe_retry_guidance.retryable_now,
              retryAction: connection.route_guidance.management.safe_retry_guidance.retry_action,
              dominantAction: connection.route_guidance.management.safe_retry_guidance.dominant_action,
            },
          },
          summary: {
            routeRole: connection.route_guidance.summary.route_role,
            handoffRoute: connection.route_guidance.summary.handoff_route,
            primaryNextStep: connection.route_guidance.summary.primary_next_step,
            dominantAction: connection.route_guidance.summary.dominant_action,
            safeRetryGuidance: {
              code: connection.route_guidance.summary.safe_retry_guidance.code,
              retryableNow: connection.route_guidance.summary.safe_retry_guidance.retryable_now,
              retryAction: connection.route_guidance.summary.safe_retry_guidance.retry_action,
              dominantAction: connection.route_guidance.summary.safe_retry_guidance.dominant_action,
            },
          },
          ledger: {
            routeRole: connection.route_guidance.ledger.route_role,
            handoffRoute: connection.route_guidance.ledger.handoff_route,
            primaryNextStep: connection.route_guidance.ledger.primary_next_step,
            dominantAction: connection.route_guidance.ledger.dominant_action,
            safeRetryGuidance: {
              code: connection.route_guidance.ledger.safe_retry_guidance.code,
              retryableNow: connection.route_guidance.ledger.safe_retry_guidance.retryable_now,
              retryAction: connection.route_guidance.ledger.safe_retry_guidance.retry_action,
              dominantAction: connection.route_guidance.ledger.safe_retry_guidance.dominant_action,
            },
          },
        }
      : null,
  });
}

export function getManagedExchangeHealth(connection: ExchangeConnection | null | undefined): ExchangeSyncHealth | null {
  if (!connection) return null;

  return resolveExchangeHealth({
    surface: 'management',
    exchange: connection.exchange,
    connectionId: connection.sync_proof_connection_id ?? connection.id,
    lastSuccessfulSyncAt: connection.last_successful_sync_at ?? connection.last_synced_at,
    syncStatus: connection.status,
    errorMessage: connection.sync_health_blocker ? connection.error_message : connection.sync_health_reason ? connection.error_message : connection.error_message,
    healthState: connection.sync_health_state,
    healthReason: connection.sync_health_reason,
    healthBlocker: connection.sync_health_blocker,
    blockerClassification: connection.sync_blocker_classification,
    freshnessBucket: connection.sync_freshness_bucket,
    freshnessStaleHours: connection.sync_freshness_stale_hours,
    freshnessState: connection.sync_freshness_state,
    primaryNextStep: connection.sync_primary_next_step,
    proofSource: 'exchange_api_connections',
    lastSyncImportedCount: connection.last_sync_imported_count,
    lastSyncSkippedCount: connection.last_sync_skipped_count,
    lastSyncErrorCount: connection.last_sync_error_count,
    lastSyncSourceLabel: connection.last_sync_source_label,
    dominantAction: connection.sync_dominant_action,
    lastSuccessfulSyncProof: connection.sync_last_successful_sync_proof
      ? {
          available: connection.sync_last_successful_sync_proof.available,
          syncedAt: connection.sync_last_successful_sync_proof.synced_at,
          freshnessBucket: connection.sync_last_successful_sync_proof.freshness_bucket,
          freshnessStaleHours: connection.sync_last_successful_sync_proof.freshness_stale_hours,
          proofSource: connection.sync_last_successful_sync_proof.proof_source,
        }
      : null,
    credentialActionRequired: connection.sync_credential_action_required,
    providerAuthActionRequired: connection.sync_provider_auth_action_required,
    providerReplayBlocked: connection.sync_provider_replay_blocked,
    runnerWaitState: connection.sync_runner_wait_state,
    runnerWaitMessage: connection.sync_runner_wait_message,
    safeRetryGuidance: connection.sync_safe_retry_guidance
      ? {
          code: connection.sync_safe_retry_guidance.code,
          retryableNow: connection.sync_safe_retry_guidance.retryable_now,
          retryAction: connection.sync_safe_retry_guidance.retry_action,
          dominantAction: connection.sync_safe_retry_guidance.dominant_action,
        }
      : null,
    proofAuthority: connection.sync_proof_authority ?? null,
    providerReplayScope: connection.sync_provider_replay_scope ?? null,
    routeGuidance: connection.sync_route_guidance
      ? {
          import: {
            routeRole: connection.sync_route_guidance.import.route_role,
            handoffRoute: connection.sync_route_guidance.import.handoff_route,
            primaryNextStep: connection.sync_route_guidance.import.primary_next_step,
            dominantAction: connection.sync_route_guidance.import.dominant_action,
            safeRetryGuidance: {
              code: connection.sync_route_guidance.import.safe_retry_guidance.code,
              retryableNow: connection.sync_route_guidance.import.safe_retry_guidance.retryable_now,
              retryAction: connection.sync_route_guidance.import.safe_retry_guidance.retry_action,
              dominantAction: connection.sync_route_guidance.import.safe_retry_guidance.dominant_action,
            },
          },
          management: {
            routeRole: connection.sync_route_guidance.management.route_role,
            handoffRoute: connection.sync_route_guidance.management.handoff_route,
            primaryNextStep: connection.sync_route_guidance.management.primary_next_step,
            dominantAction: connection.sync_route_guidance.management.dominant_action,
            safeRetryGuidance: {
              code: connection.sync_route_guidance.management.safe_retry_guidance.code,
              retryableNow: connection.sync_route_guidance.management.safe_retry_guidance.retryable_now,
              retryAction: connection.sync_route_guidance.management.safe_retry_guidance.retry_action,
              dominantAction: connection.sync_route_guidance.management.safe_retry_guidance.dominant_action,
            },
          },
          summary: {
            routeRole: connection.sync_route_guidance.summary.route_role,
            handoffRoute: connection.sync_route_guidance.summary.handoff_route,
            primaryNextStep: connection.sync_route_guidance.summary.primary_next_step,
            dominantAction: connection.sync_route_guidance.summary.dominant_action,
            safeRetryGuidance: {
              code: connection.sync_route_guidance.summary.safe_retry_guidance.code,
              retryableNow: connection.sync_route_guidance.summary.safe_retry_guidance.retryable_now,
              retryAction: connection.sync_route_guidance.summary.safe_retry_guidance.retry_action,
              dominantAction: connection.sync_route_guidance.summary.safe_retry_guidance.dominant_action,
            },
          },
          ledger: {
            routeRole: connection.sync_route_guidance.ledger.route_role,
            handoffRoute: connection.sync_route_guidance.ledger.handoff_route,
            primaryNextStep: connection.sync_route_guidance.ledger.primary_next_step,
            dominantAction: connection.sync_route_guidance.ledger.dominant_action,
            safeRetryGuidance: {
              code: connection.sync_route_guidance.ledger.safe_retry_guidance.code,
              retryableNow: connection.sync_route_guidance.ledger.safe_retry_guidance.retryable_now,
              retryAction: connection.sync_route_guidance.ledger.safe_retry_guidance.retry_action,
              dominantAction: connection.sync_route_guidance.ledger.safe_retry_guidance.dominant_action,
            },
          },
        }
      : null,
  });
}

export function getFreshnessEntryHealth(entry: ExchangeFreshnessEntry | null | undefined): ExchangeSyncHealth | null {
  if (!entry) return null;

  return resolveExchangeHealth({
    surface: 'freshness',
    exchange: entry.exchange,
    connectionId: entry.connectionId,
    lastSuccessfulSyncAt: entry.last_synced_at,
    healthState: entry.health_state,
    healthReason: entry.health_reason,
    healthBlocker: entry.health_blocker,
    blockerClassification: entry.blocker_classification,
    freshnessBucket: entry.last_synced_at ? (entry.isStale ? 'stale' : 'fresh') : 'never_synced',
    freshnessStaleHours: entry.staleHours,
    freshnessState: entry.freshness_state,
    primaryNextStep: entry.primary_next_step,
    proofSource: entry.proof_source ?? 'exchange_api_connections',
    dominantAction: entry.dominant_action,
    lastSuccessfulSyncProof: entry.last_successful_sync_proof
      ? {
          available: entry.last_successful_sync_proof.available,
          syncedAt: entry.last_successful_sync_proof.synced_at,
          freshnessBucket: entry.last_successful_sync_proof.freshness_bucket,
          freshnessStaleHours: entry.last_successful_sync_proof.freshness_stale_hours,
          proofSource: entry.last_successful_sync_proof.proof_source,
        }
      : null,
    credentialActionRequired: entry.credential_action_required,
    providerAuthActionRequired: entry.provider_auth_action_required,
    providerReplayBlocked: entry.provider_replay_blocked,
    safeRetryGuidance: entry.safe_retry_guidance
      ? {
          code: entry.safe_retry_guidance.code,
          retryableNow: entry.safe_retry_guidance.retryable_now,
          retryAction: entry.safe_retry_guidance.retry_action,
          dominantAction: entry.safe_retry_guidance.dominant_action,
        }
      : null,
    proofAuthority: entry.proof_authority ?? null,
    providerReplayScope: entry.provider_replay_scope ?? null,
    routeGuidance: entry.route_guidance
      ? {
          import: {
            routeRole: entry.route_guidance.import.route_role,
            handoffRoute: entry.route_guidance.import.handoff_route,
            primaryNextStep: entry.route_guidance.import.primary_next_step,
            dominantAction: entry.route_guidance.import.dominant_action,
            safeRetryGuidance: {
              code: entry.route_guidance.import.safe_retry_guidance.code,
              retryableNow: entry.route_guidance.import.safe_retry_guidance.retryable_now,
              retryAction: entry.route_guidance.import.safe_retry_guidance.retry_action,
              dominantAction: entry.route_guidance.import.safe_retry_guidance.dominant_action,
            },
          },
          management: {
            routeRole: entry.route_guidance.management.route_role,
            handoffRoute: entry.route_guidance.management.handoff_route,
            primaryNextStep: entry.route_guidance.management.primary_next_step,
            dominantAction: entry.route_guidance.management.dominant_action,
            safeRetryGuidance: {
              code: entry.route_guidance.management.safe_retry_guidance.code,
              retryableNow: entry.route_guidance.management.safe_retry_guidance.retryable_now,
              retryAction: entry.route_guidance.management.safe_retry_guidance.retry_action,
              dominantAction: entry.route_guidance.management.safe_retry_guidance.dominant_action,
            },
          },
          summary: {
            routeRole: entry.route_guidance.summary.route_role,
            handoffRoute: entry.route_guidance.summary.handoff_route,
            primaryNextStep: entry.route_guidance.summary.primary_next_step,
            dominantAction: entry.route_guidance.summary.dominant_action,
            safeRetryGuidance: {
              code: entry.route_guidance.summary.safe_retry_guidance.code,
              retryableNow: entry.route_guidance.summary.safe_retry_guidance.retryable_now,
              retryAction: entry.route_guidance.summary.safe_retry_guidance.retry_action,
              dominantAction: entry.route_guidance.summary.safe_retry_guidance.dominant_action,
            },
          },
          ledger: {
            routeRole: entry.route_guidance.ledger.route_role,
            handoffRoute: entry.route_guidance.ledger.handoff_route,
            primaryNextStep: entry.route_guidance.ledger.primary_next_step,
            dominantAction: entry.route_guidance.ledger.dominant_action,
            safeRetryGuidance: {
              code: entry.route_guidance.ledger.safe_retry_guidance.code,
              retryableNow: entry.route_guidance.ledger.safe_retry_guidance.retryable_now,
              retryAction: entry.route_guidance.ledger.safe_retry_guidance.retry_action,
              dominantAction: entry.route_guidance.ledger.safe_retry_guidance.dominant_action,
            },
          },
        }
      : null,
  });
}

export function getExchangeHealthLabel(state: ExchangeHealthState): 'Healthy' | 'Stale' | 'Blocked' | 'Never synced' {
  switch (state) {
    case 'healthy':
      return 'Healthy';
    case 'stale':
      return 'Stale';
    case 'blocked':
      return 'Blocked';
    case 'never_synced':
    default:
      return 'Never synced';
  }
}

export function getExchangeHealthTone(state: ExchangeHealthState): ExchangeHealthTone {
  switch (state) {
    case 'healthy':
      return 'success';
    case 'stale':
      return 'warning';
    case 'blocked':
      return 'error';
    case 'never_synced':
    default:
      return 'info';
  }
}

export function getExchangeHealthBadgeClass(state: ExchangeHealthState): string {
  switch (state) {
    case 'healthy':
      return 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
    case 'stale':
      return 'border border-amber-500/30 bg-amber-500/10 text-amber-300';
    case 'blocked':
      return 'border border-red-500/30 bg-red-500/10 text-red-400';
    case 'never_synced':
    default:
      return 'border border-blue-500/30 bg-blue-500/10 text-blue-300';
  }
}

export function formatExchangeFreshnessAge(staleHours: number | null | undefined): string {
  if (staleHours == null) return '—';
  if (staleHours >= 48) return `${Math.floor(staleHours / 24)}d old`;
  if (staleHours >= 1) return `${staleHours}h old`;
  return '<1h old';
}

export function getExchangeHealthSummaryMessage(
  health: ExchangeSyncHealth,
  surface: 'import' | 'wallet-hub' | 'management',
): string {
  const verdict = getExchangeFreshnessRecoveryVerdictKey({
    healthState: health.healthState,
    freshnessState: health.freshnessState,
    freshnessBucket: health.lastSuccessfulSyncProof.freshnessBucket,
    hasProof: health.lastSuccessfulSyncProof.available,
    blockerClassification: health.blockerClassification,
    credentialActionRequired: health.credentialActionRequired,
    providerAuthActionRequired: health.providerAuthActionRequired,
    providerReplayBlocked: health.providerReplayBlocked,
    runnerWaitState: health.runnerWaitState,
  });

  switch (verdict) {
    case 'current_again':
      return 'A recent successful sync is on record for this exchange.';
    case 'stale_verified':
      return surface === 'wallet-hub'
        ? 'The mounted sync proof is older than Pinecurl’s freshness target, but it remains the trustworthy source-to-ledger answer here.'
        : 'The mounted sync proof is older than Pinecurl’s freshness target, but it remains the trustworthy answer on this route.';
    case 'provider_auth_repair':
      return surface === 'wallet-hub'
        ? `Mounted exchange proof remains trustworthy here, and ${AUTH_SESSION_BLOCKED_MESSAGE}.`
        : `Mounted exchange proof remains trustworthy, and ${AUTH_SESSION_BLOCKED_MESSAGE}.`;
    case 'provider_credential_repair':
      return surface === 'wallet-hub'
        ? 'Mounted exchange proof remains trustworthy here, but Pinecurl needs repaired read-only credentials or permissions before live replay can continue.'
        : 'Mounted exchange proof remains trustworthy, but Pinecurl needs repaired read-only credentials or permissions before live replay can continue.';
    case 'provider_replay_window':
      return surface === 'wallet-hub'
        ? `Mounted exchange proof remains trustworthy here, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}.`
        : `Mounted exchange proof remains trustworthy, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}.`;
    case 'runner_wait':
      return surface === 'management'
        ? `Mounted exchange proof remains trustworthy, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}.`
        : `Mounted exchange proof remains trustworthy, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}.`;
    case 'never_synced':
    default:
      return 'Pinecurl has no successful sync proof for this exchange yet.';
  }
}

export function getExchangeHealthHelper(
  health: ExchangeSyncHealth,
  surface: 'import' | 'wallet-hub' | 'management',
): { eyebrow: string; message: string } | null {
  const verdict = getExchangeFreshnessRecoveryVerdictKey({
    healthState: health.healthState,
    freshnessState: health.freshnessState,
    freshnessBucket: health.lastSuccessfulSyncProof.freshnessBucket,
    hasProof: health.lastSuccessfulSyncProof.available,
    blockerClassification: health.blockerClassification,
    credentialActionRequired: health.credentialActionRequired,
    providerAuthActionRequired: health.providerAuthActionRequired,
    providerReplayBlocked: health.providerReplayBlocked,
    runnerWaitState: health.runnerWaitState,
  });

  if (verdict === 'current_again') {
    return null;
  }
  const eyebrow = getExchangeFreshnessRecoveryLabel({
    healthState: health.healthState,
    freshnessState: health.freshnessState,
    freshnessBucket: health.lastSuccessfulSyncProof.freshnessBucket,
    hasProof: health.lastSuccessfulSyncProof.available,
    blockerClassification: health.blockerClassification,
    credentialActionRequired: health.credentialActionRequired,
    providerAuthActionRequired: health.providerAuthActionRequired,
    providerReplayBlocked: health.providerReplayBlocked,
    runnerWaitState: health.runnerWaitState,
  });

  switch (verdict) {
    case 'stale_verified':
      return {
        eyebrow,
        message:
          surface === 'wallet-hub'
            ? 'The mounted sync proof stays first here even though it is older than the freshness target. Open Exchange Sync when you want a fresher provider check.'
            : 'The mounted sync proof stays first even though it is older than the freshness target. Refresh this exchange from the owner route when you want newer proof.',
      };
    case 'provider_auth_repair':
      return {
        eyebrow,
        message:
          surface === 'wallet-hub'
            ? `The mounted sync proof remains the trustworthy answer here, and ${AUTH_SESSION_BLOCKED_MESSAGE}. Repair provider sign-in in Exchange Sync before live replay can continue.`
            : `The mounted sync proof remains the trustworthy answer on this route, and ${AUTH_SESSION_BLOCKED_MESSAGE}. Repair provider sign-in before live replay can continue.`,
      };
    case 'provider_credential_repair':
      return {
        eyebrow,
        message:
          surface === 'wallet-hub'
            ? 'The mounted sync proof remains trustworthy here, but Pinecurl cannot replay live provider data until the read-only credentials or permissions are repaired in Exchange Sync.'
            : 'The mounted sync proof remains trustworthy on this route, but Pinecurl cannot replay live provider data until the read-only credentials or permissions are repaired.',
      };
    case 'provider_replay_window':
      return {
        eyebrow,
        message:
          surface === 'wallet-hub'
            ? `The mounted sync proof stays first here, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}. Follow the existing handoff back to Exchange Sync for route-owned detail.`
            : `The mounted sync proof stays first on this route, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}.`,
      };
    case 'runner_wait':
      return {
        eyebrow,
        message:
          surface === 'wallet-hub'
            ? `The mounted sync proof stays first here, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}. Follow the existing handoff back to Exchange Sync for the next retry.`
            : `The mounted sync proof stays first here, and ${SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE}.`,
      };
    case 'never_synced':
    default:
      return {
        eyebrow,
        message:
          surface === 'wallet-hub'
            ? 'A connection may exist, but Pinecurl has not recorded a successful sync for this exchange yet. Open Exchange Sync for the first successful refresh.'
            : 'Saved credentials may exist, but Pinecurl has not recorded a successful sync for this exchange yet. Run the first local sync before relying on imported counts.',
      };
  }
}

export function getExchangeSurfaceActionLabel(
  health: ExchangeSyncHealth,
  surface: 'import' | 'wallet-hub',
): string {
  if (surface === 'wallet-hub') {
    if (health.providerAuthActionRequired) {
      return 'Sign in to refresh';
    }

    if (health.credentialActionRequired) {
      return 'Reconnect exchange';
    }

    if (health.lastSuccessfulSyncProof.available) {
      return 'Review ledger proof';
    }

    switch (health.primaryNextStep) {
      case 'retry_sync':
      case 'sync_now':
        return 'Sync now';
      case 'manage_connection':
        return 'Reconnect exchange';
      default:
        return 'Review ledger proof';
    }
  }

  if (health.providerAuthActionRequired) {
    return 'Sign in to refresh';
  }

  if (health.credentialActionRequired) {
    return 'Reconnect exchange';
  }

  if (health.lastSuccessfulSyncProof.available && health.healthState !== 'never_synced') {
    return 'Review ledger proof';
  }

  switch (health.primaryNextStep) {
    case 'retry_sync':
    case 'sync_now':
      return 'Sync now';
    default:
      return health.healthState === 'blocked' ? 'Reconnect exchange' : 'Sync now';
  }
}
